import React, { ReactNode } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { SignedIn, SignedOut, RedirectToSignIn } from "@clerk/clerk-react";

// Import real components implemented in Phase 1
import { Header } from "./components/layout/Header";
import { Footer } from "./components/layout/Footer";
import { LandingPage } from "./pages/LandingPage";

// Import real page components implemented in Phase 2
// Non-coders: This imports the fully-featured dashboard page layout and device grid.
import { DashboardPage } from "./pages/DashboardPage";
import { ConfiguratorPage } from "./pages/ConfiguratorPage";

// Import real page components implemented in Phase 5
// Non-coders: This imports the real public documentation and download pages.
import { DocsPage } from "./pages/DocsPage";
import { DownloadPage } from "./pages/DownloadPage";
import { MobilePayPage } from "./pages/MobilePayPage";
import { BillingPage } from "./pages/BillingPage";
import { AdminPage } from "./pages/AdminPage";
import { useQuery } from "convex/react";
import { api } from "../convex/_generated/api";

// --- PROTECTED ROUTE COMPONENT ---
// Restricts access to authenticated users only.
// If logged in, renders the nested pages. If logged out, redirects to Clerk sign-in.
const ProtectedRoute: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <>
      <SignedIn>{children}</SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  );
};

// --- ADMIN ROUTE GUARD COMPONENT ---
// Restricts access to users verified as administrators by the backend query.
// Displays a loading spinner while verification is resolving, and redirects unauthorized users.
const AdminRoute: React.FC<{ children: ReactNode }> = ({ children }) => {
  const check = useQuery(api.admin.adminCheck);

  if (check === undefined) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "50vh",
          color: "var(--color-text-secondary)",
          gap: "1rem"
        }}
        data-testid="admin-route-loading"
      >
        <div
          style={{
            border: "3px solid rgba(255,255,255,0.1)",
            width: "24px",
            height: "24px",
            borderRadius: "50%",
            borderLeftColor: "var(--color-accent)",
            animation: "spin 1s linear infinite"
          }}
        />
        <span>Checking permissions...</span>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!check.isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// --- ERROR BOUNDARY COMPONENT ---
// Catches javascript errors anywhere in the child component tree,
// logs them, and displays a fallback UI instead of crashing the whole app.
interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log the error details to console
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div 
          style={{
            padding: "4rem 2rem",
            backgroundColor: "var(--color-bg-primary)",
            color: "var(--color-text-primary)",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "50vh",
            gap: "1.25rem"
          }}
          data-testid="error-boundary-fallback"
        >
          <span style={{ fontSize: "3.5rem" }} role="img" aria-label="Warning">⚠️</span>
          <h2 style={{ fontSize: "1.75rem", fontWeight: 700 }}>Something went wrong</h2>
          <p style={{ color: "var(--color-text-secondary)", maxWidth: "500px", fontSize: "1rem" }}>
            The application encountered an unexpected error. Please try reloading the page.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              backgroundColor: "var(--color-accent)",
              color: "var(--color-bg-primary)",
              border: "none",
              borderRadius: "var(--border-radius-md)",
              padding: "0.75rem 1.75rem",
              fontWeight: 700,
              fontSize: "0.95rem",
              cursor: "pointer",
              transition: "background-color var(--transition-speed)",
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = "var(--color-accent-hover)"}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = "var(--color-accent)"}
          >
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// --- MAIN APPLICATION SHELL ---
// Defines layout headers, footers, route mappings, and error boundaries.
export const App: React.FC = () => {
  return (
    <div 
      className="app-container" 
      style={{ 
        display: "flex", 
        flexDirection: "column", 
        minHeight: "100vh",
        backgroundColor: "var(--color-bg-primary)"
      }}
    >
      {/* Top Header Navigation */}
      <Header />

      {/* Primary Content Area wrapped in ErrorBoundary to protect against routing crashes */}
      <main style={{ flex: 1 }}>
        <ErrorBoundary>
          <Routes>
            {/* Public paths */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/docs" element={<DocsPage />} />
            <Route path="/download" element={<DownloadPage />} />
            <Route path="/mobile-pay" element={<MobilePayPage />} />

            {/* Protected paths */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/configurator"
              element={
                <ProtectedRoute>
                  <ConfiguratorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/billing"
              element={
                <ProtectedRoute>
                  <BillingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminRoute>
                    <AdminPage />
                  </AdminRoute>
                </ProtectedRoute>
              }
            />

            {/* Catch-all redirect to fallback landing page */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </ErrorBoundary>
      </main>

      {/* Global Brand Footer */}
      <Footer />
    </div>
  );
};

export default App;
